import React, { useState, useEffect } from 'react';
import { Box, Fab, Tooltip, SpeedDial, SpeedDialAction, SpeedDialIcon } from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Home, 
  Dashboard, 
  Analytics, 
  Settings, 
  Lightbulb, 
  Psychology, 
  TrendingUp, 
  Engineering,
  Close,
  ArrowBack
} from '@mui/icons-material';

// Define user progress states for type safety
type UserProgressState = 'initial' | 'input' | 'submitted' | 'selected';

interface AdaptiveNavigationProps {
  problemStatement: string;
  userProgress?: UserProgressState;
  onBack?: () => void;
}

const NavigationContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(3),
  right: theme.spacing(3),
  zIndex: 1000,
}));

const BackButtonContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(3),
  left: theme.spacing(3),
  zIndex: 1000,
}));

const StyledSpeedDial = styled(SpeedDial)(({ theme }) => ({
  '& .MuiSpeedDial-fab': {
    backgroundColor: 'rgba(138, 43, 226, 0.9)',
    '&:hover': {
      backgroundColor: 'rgba(138, 43, 226, 1)',
    },
  },
}));

const StyledFab = styled(Fab)(({ theme }) => ({
  backgroundColor: 'rgba(138, 43, 226, 0.9)',
  color: '#fff',
  '&:hover': {
    backgroundColor: 'rgba(138, 43, 226, 1)',
  },
  boxShadow: '0 5px 15px rgba(138, 43, 226, 0.3)',
}));

const AdaptiveNavigation: React.FC<AdaptiveNavigationProps> = ({ 
  problemStatement, 
  userProgress = 'initial',
  onBack 
}) => {
  const [open, setOpen] = useState(false);
  const [showContextualActions, setShowContextualActions] = useState(false);
  const [showBackButton, setShowBackButton] = useState(false);
  
  useEffect(() => {
    // Show contextual actions when a problem statement is provided
    setShowContextualActions(!!problemStatement);
    
    // Show back button when not in initial state
    setShowBackButton(userProgress !== 'initial');
  }, [problemStatement, userProgress]);
  
  const handleOpen = () => {
    setOpen(true);
  };
  
  const handleClose = () => {
    setOpen(false);
  };
  
  const handleBack = () => {
    if (onBack) {
      onBack();
    }
  };
  
  // Base navigation actions
  const baseActions = [
    { icon: <Home />, name: 'Home', tooltip: 'Go to Home' },
    { icon: <Dashboard />, name: 'Dashboard', tooltip: 'View Dashboard' },
    { icon: <Analytics />, name: 'Analytics', tooltip: 'View Analytics' },
    { icon: <Settings />, name: 'Settings', tooltip: 'Settings' },
  ];
  
  // Contextual actions that appear when a problem is being solved
  const contextualActions = [
    { icon: <Lightbulb />, name: 'Ideas', tooltip: 'Generate Ideas' },
    { icon: <Psychology />, name: 'Analysis', tooltip: 'Strategic Analysis' },
    { icon: <TrendingUp />, name: 'Growth', tooltip: 'Growth Planning' },
    { icon: <Engineering />, name: 'Technical', tooltip: 'Technical Implementation' },
  ];
  
  // Combine actions based on context
  const actions = showContextualActions 
    ? [...contextualActions, ...baseActions]
    : baseActions;
  
  return (
    <>
      <NavigationContainer>
        <AnimatePresence>
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <StyledSpeedDial
              ariaLabel="Navigation menu"
              icon={<SpeedDialIcon icon={<Home />} openIcon={<Close />} />}
              onClose={handleClose}
              onOpen={handleOpen}
              open={open}
              direction="up"
              FabProps={{
                sx: {
                  boxShadow: '0 5px 15px rgba(138, 43, 226, 0.3)',
                }
              }}
            >
              {actions.map((action) => (
                <SpeedDialAction
                  key={action.name}
                  icon={action.icon}
                  tooltipTitle={action.tooltip}
                  tooltipOpen
                  onClick={handleClose}
                  FabProps={{
                    sx: {
                      bgcolor: action.name === 'Ideas' || action.name === 'Analysis' || action.name === 'Growth' || action.name === 'Technical'
                        ? 'rgba(255, 111, 0, 0.9)'
                        : 'rgba(26, 26, 46, 0.9)',
                      '&:hover': {
                        bgcolor: action.name === 'Ideas' || action.name === 'Analysis' || action.name === 'Growth' || action.name === 'Technical'
                          ? 'rgba(255, 111, 0, 1)'
                          : 'rgba(26, 26, 46, 1)',
                      },
                    }
                  }}
                />
              ))}
            </StyledSpeedDial>
          </motion.div>
        </AnimatePresence>
      </NavigationContainer>
      
      {showBackButton && (
        <BackButtonContainer>
          <AnimatePresence>
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Tooltip title="Go Back" placement="right">
                <StyledFab
                  size="medium"
                  aria-label="go back"
                  onClick={handleBack}
                >
                  <ArrowBack />
                </StyledFab>
              </Tooltip>
            </motion.div>
          </AnimatePresence>
        </BackButtonContainer>
      )}
    </>
  );
};

export default AdaptiveNavigation; 